import {Injectable} from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import CryptoEs from 'crypto-es';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import {User} from '@models/user/user';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, of, throwError} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {LogoutUrl} from '@models/user/logoutUrl';
import {StorageService} from '@services/storage/storage.service';
import {Router} from '@angular/router';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {Agreement} from '@models/user/agreement';
import {TranslateService} from '@ngx-translate/core';
import {EnvironmentConfig} from '@environments/environment';


@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthenticationService {
  private USER_URL_SUFFIX: string = '/api/web/user';
  private SIGN_IN_URL_SUFFIX: string = this.USER_URL_SUFFIX + '/signIn';
  private SIGN_OUT_URL_SUFFIX: string = this.USER_URL_SUFFIX + '/logout';
  private AGREEMENT_URL_SUFFIX: string = this.USER_URL_SUFFIX + '/agreement';
  private USER_LANGUAGE_URL_SUFFIX: string = this.USER_URL_SUFFIX + '/:id/language';
  private SAML_LOGIN_URL_SUFFIX: string = this.USER_URL_SUFFIX + '/saml/url';
  private SET_NOTIFICATION_RETENTION_URL_SUFFIX = '/api/web/user/:userId/notification/retention/:days';
  private GET_MAX_NOTIFICATION_RETENTION_URL_SUFFIX = '/api/web/notification/retentionmax';
  private CHANGE_PASSWORD_URL_SUFFIX = '/api/web/user/:userId/password';

  private readonly BASE_URL: string;
  private responseCache = new Map();

  constructor(public afAuth: AngularFireAuth, // Inject Firebase auth service
              private http: HttpClient,
              private apiLocationService: ApiLocationService,
              private storageService: StorageService,
              private router: Router,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private translate: TranslateService) {
    this.BASE_URL = this.apiLocationService.getLocation();
  }

  // Sign in with email/password
  public signIn(email: string, password: string) {
    const credentialHash = this.getPasswordEncryptedHash(password);
    return this.afAuth.signInWithEmailAndPassword(email, credentialHash);
  }

  public persistFirebaseDetails(userCredential: any, captchaToken: string): Observable<any> {
    if (!userCredential?.user?.auth?.currentUser?.accessToken || !captchaToken) {
      return throwError(() => new Error('Invalid user credentials object'));
    }
    const user = userCredential.user;
    const firebaseAuthorizationProperties = {
      firebaseUserUid: user.uid,
      accessToken: user.auth.currentUser.accessToken,
      refreshToken: user.refreshToken,
      captchaToken: captchaToken
    };
    const url: string = this.BASE_URL + this.SIGN_IN_URL_SUFFIX;
    return this.http.put<User>(url, firebaseAuthorizationProperties, HTTPS_REQUEST_OPTIONS);
  }

  // Sign out
  public signOutUser() {
    const logoutFallbackUrl = this.getLogoutFallbackUrl();
    this.afAuth.signOut().then((_success) => {

      this.getLogoutUrl().subscribe({
        next: (response: LogoutUrl) => {
          this.clear();
          this.loadingIndicator.hide();
          this.navigateTo(response.value);
        }, error: (error: HttpResponse<any>) => {
          console.error('UserService. logout failed', error);
          this.clear();
          this.loadingIndicator.hide();
          this.navigateTo(logoutFallbackUrl);
        }
      });

    }, (error) => {
      this.loadingIndicator.hide();
      console.error('firebase logout failed. ', error);
      this.navigateTo(logoutFallbackUrl);
    });
  }

  private getLogoutFallbackUrl(): string {
    if (EnvironmentConfig.PortalSettings.ssoEnabled) {
      return 'samllogin';
    }
    return 'login';

  }

  private navigateTo(route: string): void {
    this.router.navigate([route]).then().catch((error) => {
      console.error('Error navigating to route ' + route, error);
    });
  }

  public resetPassword(passwordResetEmail: string) {
    return this.afAuth.sendPasswordResetEmail(passwordResetEmail);
  }

  // Sign in with Google
  public googleSignIn() {
    return this.afAuth.signInWithPopup(new firebase.auth.GoogleAuthProvider);
  }

  // Sign in with Google
  public facebookSignIn() {
    return this.afAuth.signInWithPopup(new firebase.auth.FacebookAuthProvider);
  }

  public getAgreement(): Observable<any> {
    const url: string = this.BASE_URL + this.AGREEMENT_URL_SUFFIX;
    console.debug('agreement url: ' + url);
    return this.http.get<Agreement>(url, HTTPS_REQUEST_OPTIONS);
  }

  public setAgreementAccepted(): Observable<any> {
    const url: string = this.BASE_URL + this.AGREEMENT_URL_SUFFIX + '/true';
    console.debug('agreement accepted url: ' + url);
    return this.http.post<Agreement>(url, null, HTTPS_REQUEST_OPTIONS);
  }

  public saveUserLanguage(user: User, language: string): void {
    let url: string = this.BASE_URL + this.USER_LANGUAGE_URL_SUFFIX;
    url = url.replace(':id', user.firebaseUid);
    user.language = language;
    console.debug('saml login url: ' + url);
    this.http.post(url, user, HTTPS_REQUEST_OPTIONS).subscribe(
        {
          next: _response => {
            console.log('Language changed to: ' + language);
          }, error: error => {
            console.error('Error changing language to: ' + language, JSON.stringify(error));
          }
        });
  }

  public getLoggedUserDetails(): Observable<any> {
    const url: string = this.BASE_URL + this.USER_URL_SUFFIX;

    const userFromCache = this.responseCache.get(url);
    if (userFromCache) {
      return of(userFromCache);
    }
    console.debug('user details url: ' + url);
    const response = this.http.get<User>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }), map(user => new User(user)));
    response.subscribe(user => this.responseCache.set(url, user));
    return response;
  }

  public samlLogin(): Observable<any> {
    const url: string = this.BASE_URL + this.SAML_LOGIN_URL_SUFFIX;
    console.debug('saml login url: ' + url);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public verifyPasswordResetCode(actionCode: string) {
    return this.afAuth.verifyPasswordResetCode(actionCode);
  }

  public confirmPasswordReset(actionCode: string, newPassword: string) {
    const passwordHash = this.getPasswordEncryptedHash(newPassword);
    return this.afAuth.confirmPasswordReset(actionCode, passwordHash);
  }

  public setNotificationRetention(days: number): Observable<any> {
    const id = -1;
    let url: string = this.BASE_URL + this.SET_NOTIFICATION_RETENTION_URL_SUFFIX;
    url = url.replace(':userId', String(id))
    .replace(':days', String(days));
    const request = {
      days,
      id
    };
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public getMaxNotificationRetention(): Observable<any> {
    const url: string = this.BASE_URL + this.GET_MAX_NOTIFICATION_RETENTION_URL_SUFFIX;
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public changePassword(user: User): Observable<any> {

    user.credentialHash = this.getPasswordEncryptedHash(user.credentialHash);
    let url: string = this.BASE_URL + this.CHANGE_PASSWORD_URL_SUFFIX;
    url = url.replace(':userId', user.firebaseUid);

    return this.http.post(url, user, HTTPS_REQUEST_OPTIONS);
  }

  public parseFirebaseResponseErrorCode(error: any): string {
    if (!error?.code) {
      return '';
    }
    const errorCode = error.code;

    let message: string;

    switch (errorCode) {
      case 'auth/wrong-password':
        message = this.translate.instant('The password is invalid or the user does not have a password.');
        break;
      case 'auth/network-request-failed':
        message = this.translate.instant('Please check your internet connection');
        break;
      case 'auth/too-many-requests':
        message = this.translate.instant('We have detected too many requests from your device. Take a break please!');
        break;
      case 'auth/user-disabled':
        message = this.translate.instant('Your account has been disabled or deleted. Please contact the system administrator.');
        break;
      case 'auth/requires-recent-login':
        message = this.translate.instant('Please login again and try again!');
        break;
      case 'auth/email-already-exists':
        message = this.translate.instant('Email address is already in use by an existing user.');
        break;
      case 'auth/user-not-found':
        message = this.translate.instant('We could not find user account associated with the email address or phone number.');
        break;
      case 'auth/phone-number-already-exists':
        message = this.translate.instant('The phone number is already in use by an existing user.');
        break;
      case 'auth/invalid-phone-number':
        message = this.translate.instant('The phone number is not a valid phone number!');
        break;
      case 'auth/invalid-email  ':
        message = this.translate.instant('The email address is not a valid email address!');
        break;
      case 'auth/cannot-delete-own-user-account':
        message = this.translate.instant('You cannot delete your own user account.');
        break;
      default:
        message = this.translate.instant('Something went wrong. Please try again later.');
        break;
    }
    return message;
  }

  public clear(): void {
    this.responseCache = new Map();
    this.storageService.clearStorage();
    this.leftMenuService.resetCache();
  }

  private getPasswordEncryptedHash(password: string | null): string {
    if (!password) {
      return '';
    }
    return CryptoEs.SHA256(password).toString(CryptoEs.enc.Hex).toUpperCase();
  }


  private getLogoutUrl(): Observable<any> {
    const url: string = this.BASE_URL + this.SIGN_OUT_URL_SUFFIX;
    console.debug('logout url: ' + url);
    return this.http.post<LogoutUrl>(url, null, HTTPS_REQUEST_OPTIONS);
  }
}
